import React, { useEffect, useState, useContext } from 'react';
import { Tabs } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';

import {
  EmptyProject,
  IBubbleSet,
  IProject,
  ISupportLanguage,
} from 'utils/types/storyTypes';
import SupportLangContext from 'context/SupportLangContext';

import DefaultLayout from 'components/layout/DefaultLayout';
import DefaultTabs from 'components/tab/DefaultTabs';

import LoadingImgTable from '../loadingImage/LoadingImgTable';

import ResourceMainPage from '../resource/ResourceMainPage';

import EpisodeTable from '../episode/EpisodeTable';
import OtomeInformation from './otome/OtomeInformation';

import DLCMainTable from '../dlc/DLCMainTable';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  ArchiveBook,
  BookSaved,
  Candle,
  Chart2,
  GalleryExport,
  Gift,
  UserTag,
} from 'iconsax-react';
import { theme } from 'utils/style/theme';
import OperationMainPage from '../operation/OperationMainPage';
import { storyApi } from 'apis/story/storyApis';
import CharactersMainPage from '../characters/CharactersMainPage';
/**
 * * 작품 상세 정보 & 에피소드 리스트 & 메인 탭(에피, 리소스 ...)
 */

interface IProps {
  dlcMenu?: boolean;
}
const ProjectDetailPage = ({ dlcMenu }: IProps) => {
  const { t } = useTranslation();
  const [cookies, setCookie] = useCookies();
  const { lang, previousPageType } = cookies;

  // path='/story/:projectId/:pageType'
  const { projectId, pageType = previousPageType } = useParams(); // URL 파라매터 (프로젝트ID, 페이지)

  const navigate = useNavigate();

  const location = useLocation() as any;
  const langList = useContext(SupportLangContext);

  const [currentProject, setCurrentProject] = useState<IProject>(EmptyProject); // 선택중인 프로젝트
  const [bubbleSetList, setBubbleSetList] = useState<IBubbleSet[]>(); // 말풍선 목록..?

  // 상위탭, 하위탭 이동을 위한 key.
  const [activeTabKey, setActiveTabKey] = useState<string | undefined>(
    pageType,
  );

  const [resourceRefreshCounter, setResourceRefreshCounter] =
    useState<number>(0);
  const [characterRefreshCounter, setCharacterRefreshCounter] =
    useState<number>(0);
  const [operationRefreshCounter, setOperationRefreshCounter] =
    useState<number>(0);

  const [draggingStatus, setDraggingStatus] = useState(0); // 정렬 후 상태가 바꼈다는걸 알려주기 위함
  const [dlcMainStatus, setDlcMainStatus] = useState<boolean>(false); // false일 경우, dlc main table이 나타난다.
  const [supportLangList, setSupportLangList] = useState<
    ISupportLanguage[] | null
  >(null);

  /// 작품 수정 화면으로 이동
  const handleUpdateClick = () => {
    console.log(`handleUpdateClick : `, currentProject);

    navigate(`/story/${projectId}/otome/update`, {
      state: {
        project: currentProject,
        bubbleList: bubbleSetList,
      },
    });
  };

  /// 탭 누를때
  const handleTabClick = (activeKey) => {
    console.log(`상세페이지 탭 : `, activeKey);

    setActiveTabKey(activeKey);

    if (activeKey == 'resource')
      setResourceRefreshCounter(resourceRefreshCounter + 1);
    if (activeKey == 'character')
      setCharacterRefreshCounter(characterRefreshCounter + 1);
    if (activeKey == 'operation')
      setOperationRefreshCounter(operationRefreshCounter + 1);

    setCookie('previousPageType', activeKey); // 쿠키로 저장

    navigate(`/story/${projectId}/${activeKey}`, {
      state: {
        tabKey: `${activeKey}`,
        record: { project_type: location?.state?.record?.project_type },
      },
    });
  };

  const fetchData = async () => {
    const resData = await storyApi.storyDetail(projectId);

    setCurrentProject(resData.project);
  };

  useEffect(() => {
    fetchData().catch(console.log);
  }, [lang]);

  const fetchBubbleListData = async () => {
    const res = await storyApi.bubbleList('KO');
    setBubbleSetList(res);
  };

  useEffect(() => {
    console.log(`ProjectDetailPage.tsx useEffect :: ${projectId}/${pageType}`);
    setActiveTabKey(pageType);
    handleTabClick(pageType);

    fetchBubbleListData(); // TODO useQuery로 교체해주기
    setSupportLangList(langList);
  }, []);

  // * 작품 상세정보 상단 탭 메뉴
  const OtomeTabMenuList = [
    {
      // 에피소드
      key: 'episode',
      tab: (
        <TabPineWrapper>
          <BookSaved
            size='24'
            color={
              activeTabKey === 'episode'
                ? theme.colors.blue_500
                : theme.colors.dark_300
            }
            variant={activeTabKey === 'episode' ? 'Bold' : 'Linear'}
          />
          {t('storyEpisodeID.storyTabMenuList.episode')}
        </TabPineWrapper>
      ),
      table: (
        <EpisodeTable projectId={projectId} selectProject={currentProject} />
      ),
    },
    {
      // 리소스
      key: 'resource',
      tab: (
        <TabPineWrapper>
          <Gift
            size='24'
            color={
              activeTabKey === 'resource'
                ? theme.colors.blue_500
                : theme.colors.dark_300
            }
            variant={activeTabKey === 'resource' ? 'Bold' : 'Linear'}
          />
          {t('storyEpisodeID.storyTabMenuList.resource')}
        </TabPineWrapper>
      ),
      table: (
        <ResourceMainPage
          currentProject={currentProject}
          refreshCounter={resourceRefreshCounter}
        />
      ),
    },
    {
      // 등장인물
      key: 'character',
      tab: (
        <TabPineWrapper>
          <UserTag
            size='24'
            color={
              activeTabKey === 'character'
                ? theme.colors.blue_500
                : theme.colors.dark_300
            }
            variant={activeTabKey === 'character' ? 'Bold' : 'Linear'}
          />
          {t('storyEpisodeID.storyTabMenuList.characters')}
        </TabPineWrapper>
      ),
      table: (
        <CharactersMainPage
          currentProject={currentProject}
          refreshCounter={characterRefreshCounter}
        />
      ),
    },
    {
      // 로딩 화면
      key: 'loading',
      tab: (
        <TabPineWrapper>
          <GalleryExport
            size='24'
            color={
              activeTabKey === 'loading'
                ? theme.colors.blue_500
                : theme.colors.dark_300
            }
            variant={activeTabKey === 'loading' ? 'Bold' : 'Linear'}
          />
          {t('storyEpisodeID.storyTabMenuList.loading')}
        </TabPineWrapper>
      ),
      table: <LoadingImgTable />,
    },
    {
      key: 'dlc',
      tab: (
        <TabPineWrapper>
          <ArchiveBook
            size='24'
            color={
              activeTabKey === 'dlc'
                ? theme.colors.blue_500
                : theme.colors.dark_300
            }
            variant={activeTabKey === 'dlc' ? 'Bold' : 'Linear'}
          />

          {t('storyEpisodeID.storyTabMenuList.DLCManagement')}
        </TabPineWrapper>
      ),
      table: (
        <DLCMainTable
          dlcMainStatus={dlcMainStatus}
          setDlcMainStatus={setDlcMainStatus}
          projectType='otome'
          selectProject={currentProject}
          draggingStatus={draggingStatus}
          setDraggingStatus={setDraggingStatus}
        />
      ),
    },
    {
      key: 'operation',
      tab: (
        <TabPineWrapper>
          <Candle
            size='24'
            color={
              activeTabKey === 'operation'
                ? theme.colors.blue_500
                : theme.colors.dark_300
            }
            variant={activeTabKey === 'operation' ? 'Bold' : 'Linear'}
          />
          {t('storyEpisodeID.storyTabMenuList.operation')}
        </TabPineWrapper>
      ),
      table: (
        <OperationMainPage
          currentProject={currentProject}
          refreshCounter={operationRefreshCounter}
        />
      ),
    },
    {
      key: 'dashboard',
      tab: (
        <TabPineWrapper>
          <Chart2
            size='24'
            color={
              activeTabKey === 'dashboard'
                ? theme.colors.blue_500
                : theme.colors.dark_300
            }
            variant={activeTabKey === 'dashboard' ? 'Bold' : 'Linear'}
          />
          {t('storyEpisodeID.storyTabMenuList.dashboard')}
        </TabPineWrapper>
      ),
    },
  ];

  return (
    <DefaultLayout>
      <OtomeInformation
        onClick={handleUpdateClick}
        selectProject={currentProject}
        bubbleSetList={bubbleSetList}
        supportLangList={supportLangList}
      />
      <DefaultTabs type='card' onChange={handleTabClick} activeKey={pageType}>
        {OtomeTabMenuList?.map((v) => (
          <Tabs.TabPane key={v.key} tab={v.tab}>
            {v.table}
          </Tabs.TabPane>
        ))}
      </DefaultTabs>
    </DefaultLayout>
  );
};

export default ProjectDetailPage;

const TabPineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  & > * + * {
    margin-top: 4px;
  }
`;
